import { useEffect, useRef, useState } from "react";
import { EMPTY_DATA_CATALOG, FlussDataCatalog, fetchDataCatalog } from "modules/fluss/services/fluss-api";
import { buildFlowGraph } from "./fluss-node-panel-utils";

const DEBOUNCE_MS = 400;

// Single authoring catalog (C7/C9): record + process variables + outputs of the ancestor nodes,
// fetched from the live canvas (the backend has no other way to see unsaved edits). It refetches
// when the selected node changes AND whenever the graph changes (bpmn-js `commandStack.changed`),
// both debounced, so the catalog does not go stale while the same panel stays open.
// Errors degrade to the empty catalog; the panel must keep working.
export const useDataCatalog = (
    modeler: any,
    nodeId: string | null | undefined,
    definitionKey?: string
): FlussDataCatalog => {
    const [catalog, setCatalog] = useState<FlussDataCatalog>(EMPTY_DATA_CATALOG);
    const [graphVersion, setGraphVersion] = useState(0);
    // A response for a node the caller already navigated away from must never overwrite the
    // catalog of the node selected now.
    const requestTokenRef = useRef(0);

    useEffect(() => {
        const eventBus = typeof modeler?.get === "function" ? modeler.get("eventBus") : null;
        if (!eventBus || typeof eventBus.on !== "function") return undefined;
        const handler = () => setGraphVersion((version) => version + 1);
        eventBus.on("commandStack.changed", handler);
        return () => eventBus.off?.("commandStack.changed", handler);
    }, [modeler]);

    useEffect(() => {
        const token = ++requestTokenRef.current;
        if (!nodeId) {
            setCatalog(EMPTY_DATA_CATALOG);
            return undefined;
        }

        const timer = setTimeout(() => {
            fetchDataCatalog(buildFlowGraph(modeler), nodeId, definitionKey)
                .then((result) => {
                    if (requestTokenRef.current === token) setCatalog(result || EMPTY_DATA_CATALOG);
                })
                .catch(() => {
                    if (requestTokenRef.current === token) setCatalog(EMPTY_DATA_CATALOG);
                });
        }, DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [nodeId, modeler, definitionKey, graphVersion]);

    return catalog;
};

export default useDataCatalog;
